import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRICING = [
  { key: "yes", label: "Yes, I'd pay for this" },
  { key: "maybe", label: "Maybe, depends on price" },
  { key: "no", label: "Not right now" },
];

export default function FeedbackDialog({ open, onOpenChange }) {
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [pricing, setPricing] = useState("");
  const [wouldPay, setWouldPay] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/feedback", {
        rating: rating || null,
        message: message.trim() || null,
        pricing_interest: pricing || null,
        would_pay: wouldPay.trim() || null,
      });
      toast.success("Thank you. Your feedback shapes the beta.");
      onOpenChange(false);
      setRating(0); setMessage(""); setPricing(""); setWouldPay("");
    } catch {
      toast.error("Could not send feedback. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Share your feedback</DialogTitle>
          <DialogDescription>You're in the closed beta. Tell us what's working and what isn't.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-sm font-medium">How's your experience so far?</p>
            <div className="flex gap-1.5" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} aria-label={`${n} stars`} data-testid={`rating-${n}`}
                  className="transition-transform duration-150 hover:scale-110">
                  <Star size={24} className={n <= rating ? "fill-brand text-brand" : "text-slate-300"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">What would you tell us?</label>
            <textarea data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder="Anything, big or small." className="w-full resize-none rounded-lg border border-brand-line bg-white p-3 text-sm focus:border-brand focus:outline-none" />
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium">Would you pay for Launch to Income?</p>
            <div className="grid gap-2" data-testid="feedback-pricing">
              {PRICING.map((p) => (
                <button key={p.key} onClick={() => setPricing(p.key)} data-testid={`pricing-${p.key}`}
                  className={`rounded-lg border p-2.5 text-left text-sm transition-all duration-200 ${pricing === p.key ? "border-brand bg-brand-surface" : "border-brand-line bg-white hover:border-brand-muted"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {pricing && pricing !== "no" && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">What feels like a fair price?</label>
              <input data-testid="feedback-price" value={wouldPay} onChange={(e) => setWouldPay(e.target.value)}
                placeholder="e.g. $15/mo, or $49 one-time" className="w-full rounded-lg border border-brand-line bg-white p-2.5 text-sm focus:border-brand focus:outline-none" />
            </div>
          )}
          <button data-testid="feedback-submit" onClick={submit} disabled={saving}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null} Send feedback
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
